import React from "react"
import { useTranslation } from "react-i18next"

import "@/i18n"
import { useClassFacetDescriptors } from "@/components/search/classes/facet-descriptors"
import { useEducatorFacetDescriptors } from "@/components/search/educators/facet-descriptors"
import type { TermFacetDescriptor } from "@/components/search/facet-descriptor"
import { createAttributeOptions } from "@/constants/speciality"
import {
  useFilter,
  useFilterRegistration,
  useHits,
  usePagination,
  useSearchContext,
  useSearchStatus,
} from "@/lib/redux-search"
import SearchProvider from "@/lib/redux-search/search-provider"
import useSearch from "@/lib/redux-search/use-search"
import { typedEntries, typedValues } from "@/types/helpers"
import type { TagHierarchy } from "@/types/tag-hierarchy"

import { classHitSchema, educatorHitSchema } from "./schemas"
import type {
  ClassMember,
  ClassMemberEducator,
  EducatorMember,
  FacetDescriptor,
  MembersCommands,
  MembersState,
  ShortlistMember,
  ShortlistMemberKind,
  ShortlistMembers,
  ShortlistMembersProviderProps,
} from "./types"

const TEXT_FILTER_ID = "name"
const MIN_TEXT_LENGTH = 2

const KINDS: Record<
  ShortlistMemberKind,
  { path: string; source: string[]; textField: string }
> = {
  class: {
    path: "classes",
    source: [
      "class_details.name",
      "class_details.curriculum_key",
      "class_details.subject_key",
      "class_details.class_size_range",
      "profile_details.name",
      "id",
      "result.listed_class_offerings",
      "result.subject_id",
      "result.timezone_id",
      "result.listing_profile.listing_type",
      "signed_identifier",
    ],
    textField: "class_details.name",
  },
  educator: {
    path: "educators",
    source: [
      "name",
      "listing_type",
      "speciality_attributes",
      "tags",
      "id",
      "signed_identifier",
    ],
    textField: "name",
  },
}

const MembersContext = React.createContext<ShortlistMembers | null>(null)

/**
 * Browses one shortlist's direct members of one kind. Owns a redux-search
 * store of its own and writes nothing to the host page's URL, so a consumer
 * can mount it anywhere in a React tree.
 */
export function ShortlistMembersProvider({
  shortlistId,
  kind,
  includeEducatorClasses = false,
  children,
}: ShortlistMembersProviderProps) {
  const { path, source } = KINDS[kind]
  const searchMethods = useSearch({
    url: `/search/shortlists/${shortlistId}/${path}/query`,
    source,
  })
  const [searchError, setSearchError] = React.useState<unknown>(null)

  // Every search, whether the provider's own or one a command starts,
  // reports its failure here; a superseded search is not a failure.
  const observedSearchMethods = React.useMemo(
    () => ({
      ...searchMethods,
      search() {
        const outcome = searchMethods.search()
        outcome.then(
          () => setSearchError(null),
          (error: unknown) => {
            if (isAbort(error)) return
            setSearchError(error)
          },
        )
        return outcome
      },
    }),
    [searchMethods],
  )

  React.useEffect(() => {
    observedSearchMethods.setOptions({
      educator_classes: includeEducatorClasses,
    })
    observedSearchMethods.search()
  }, [observedSearchMethods, includeEducatorClasses])

  const Members = kind === "class" ? ClassMembers : EducatorMembers

  return (
    <SearchProvider {...observedSearchMethods}>
      <Members searchError={searchError}>{children}</Members>
    </SearchProvider>
  )
}

export function useShortlistMembers(): ShortlistMembers {
  const members = React.use(MembersContext)
  if (members === null) {
    throw new Error(
      "useShortlistMembers must be used within ShortlistMembersProvider",
    )
  }
  return members
}

function isAbort(error: unknown): boolean {
  return error instanceof Error && error.message === "search aborted"
}

type MembersProps = React.PropsWithChildren<{ searchError: unknown }>

function ClassMembers({ searchError, children }: MembersProps) {
  const facets = useClassFacetDescriptors()
  const mapHit = useClassMemberMapper()

  return (
    <MembersStateProvider
      facets={facets}
      mapHit={mapHit}
      textField={KINDS.class.textField}
      searchError={searchError}
    >
      {children}
    </MembersStateProvider>
  )
}

function EducatorMembers({ searchError, children }: MembersProps) {
  const facets = useEducatorFacetDescriptors()
  const mapHit = useEducatorMemberMapper()

  return (
    <MembersStateProvider
      facets={facets}
      mapHit={mapHit}
      textField={KINDS.educator.textField}
      searchError={searchError}
    >
      {children}
    </MembersStateProvider>
  )
}

function MembersStateProvider({
  facets,
  mapHit,
  textField,
  searchError,
  children,
}: React.PropsWithChildren<{
  facets: TermFacetDescriptor[]
  mapHit: (hit: unknown) => ShortlistMember
  textField: string
  searchError: unknown
}>) {
  useFilterRegistration(TEXT_FILTER_ID, { type: "name", field: textField })
  const textFilter = useFilter(TEXT_FILTER_ID)
  const hits = useHits<unknown>()
  const { wasSearched, loading } = useSearchStatus()
  const pagination = usePagination()
  const { clearAllFilters, search } = useSearchContext()

  const mapped = React.useMemo(() => {
    try {
      return { members: hits.map(mapHit), error: null }
    } catch (error) {
      return { members: [], error }
    }
  }, [hits, mapHit])

  const state = React.useMemo((): MembersState => {
    if (searchError || mapped.error) return { status: "error" }
    if (!wasSearched) return { status: "loading" }
    return {
      status: "ready",
      refreshing: loading,
      members: mapped.members,
      page: {
        number: pagination.info.pageNumber,
        count: pagination.info.pageCount,
        start: pagination.info.pageStart,
        end: pagination.info.pageEnd,
        total: pagination.info.resultCount,
      },
      facets: facets.map(describeFacet),
      text: textFilter.values[0] ?? "",
    }
  }, [
    searchError,
    mapped,
    wasSearched,
    loading,
    pagination.info,
    facets,
    textFilter.values,
  ])

  const commands = React.useMemo((): MembersCommands => {
    const facetById = (facetId: string) => {
      const facet = facets.find((candidate) => candidate.id === facetId)
      if (facet === undefined) {
        throw new Error(`no facet "${facetId}" among the shortlist's members`)
      }
      return facet
    }
    return {
      toggleFacet(facetId, value) {
        facetById(facetId).toggle(value)
      },
      clearFacet(facetId) {
        facetById(facetId).clear()
      },
      setText(value) {
        textFilter.setValues(value.length < MIN_TEXT_LENGTH ? [] : [value])
        pagination.setPage(1)
        search()
      },
      setPage(page) {
        pagination.setPage(page)
        search()
      },
      clearAll() {
        clearAllFilters()
        pagination.setPage(1)
        search()
      },
    }
  }, [facets, textFilter, pagination, search, clearAllFilters])

  const members = React.useMemo(() => ({ state, commands }), [state, commands])

  return <MembersContext value={members}>{children}</MembersContext>
}

function describeFacet(facet: TermFacetDescriptor): FacetDescriptor {
  return {
    id: facet.id,
    label: facet.label,
    options: facet.options.map(({ value, label, count, selected }) => ({
      value,
      label,
      count,
      selected,
    })),
  }
}

function useClassMemberMapper(): (hit: unknown) => ClassMember {
  const { t: tCurricula } = useTranslation("subjects", {
    keyPrefix: "curricula",
  })
  const { t: tSubjects } = useTranslation("subjects", { keyPrefix: "subjects" })

  return React.useCallback(
    (raw: unknown): ClassMember => {
      const hit = classHitSchema.parse(raw)
      const { class_details: details, result } = hit
      // A class with no listed offering is offered by its listing profile.
      const [first, ...rest] = result.listed_class_offerings.map(
        (offering): ClassMemberEducator => ({
          id: offering.teacher_id,
          name: offering.teacher_name,
        }),
      )
      const educators: ClassMember["educators"] =
        first === undefined
          ? [{ id: null, name: hit.profile_details.name }]
          : [first, ...rest]

      return {
        kind: "class",
        key: hit.id,
        name: details.name,
        curriculumLabel: tCurricula(details.curriculum_key as never),
        subjectLabel:
          details.subject_key === null
            ? null
            : tSubjects(details.subject_key as never),
        classSize: {
          min: details.class_size_range.gte,
          max: details.class_size_range.lte,
        },
        timezone: result.timezone_id,
        listingProfile: {
          name: hit.profile_details.name,
          type: result.listing_profile.listing_type,
        },
        educators,
        selection: {
          kind: "class",
          signedIdentifier: hit.signed_identifier,
          subjectId: result.subject_id,
        },
      }
    },
    [tCurricula, tSubjects],
  )
}

function useEducatorMemberMapper(): (hit: unknown) => EducatorMember {
  const { t: tTags } = useTranslation("listing_profile_tags")
  const specialityOptions = React.useMemo(
    () => createAttributeOptions("search:select_attributes"),
    [],
  )
  const tagHierarchy = React.useMemo(
    () => tTags("tag_categories", { returnObjects: true }) as TagHierarchy,
    [tTags],
  )

  return React.useCallback(
    (raw: unknown): EducatorMember => {
      const hit = educatorHitSchema.parse(raw)
      const tagSet = new Set(hit.tags)

      return {
        kind: "educator",
        key: hit.id,
        name: hit.name,
        listingType: hit.listing_type,
        specialityLabels: specialityOptions
          .filter((option) => hit.speciality_attributes.includes(option.value))
          .map((option) => option.label),
        tagGroups: typedValues(tagHierarchy).flatMap(
          ({ name, tag_subcategories }) => {
            const tags = typedValues(tag_subcategories).flatMap(({ tags }) =>
              typedEntries(tags).flatMap(([key, tag]) =>
                tagSet.has(key) ? tag.name : [],
              ),
            )
            return tags.length === 0 ? [] : [{ category: name, tags }]
          },
        ),
        selection: {
          kind: "educator",
          signedIdentifier: hit.signed_identifier,
        },
      }
    },
    [specialityOptions, tagHierarchy],
  )
}
